import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  NewsArticle, 
  NewsCategory, 
  UserLocation, 
  NewsPreferences, 
  NewsContextType 
} from '../types/news';
import { useAuth } from './AuthContext';
import { 
  fetchNews, 
  fetchLocalNews, 
  getUserLocation,
  fetchNewsByCategory 
} from '../services/newsService';
import { toast } from 'react-hot-toast';

const DEFAULT_PREFERENCES: NewsPreferences = {
  preferredProviders: ['the-times-of-india', 'economic-times', 'the-hindu', 'bbc-news'],
  preferredCategories: ['general', 'business', 'technology', 'local'],
  preferredCountries: ['in', 'us'],
  autoRefresh: true,
  refreshInterval: 30
};

const NewsContext = createContext<NewsContextType | undefined>(undefined);

export function NewsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [savedArticles, setSavedArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [newsPreferences, setNewsPreferences] = useState<NewsPreferences>(DEFAULT_PREFERENCES);

  // Load user preferences and location
  useEffect(() => {
    if (user) {
      loadUserPreferences();
      loadUserLocation();
      loadSavedArticles();
    }
  }, [user]);

  // Auto-refresh news
  useEffect(() => {
    if (newsPreferences.autoRefresh && user) {
      const interval = setInterval(() => {
        refreshNews();
      }, newsPreferences.refreshInterval * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [newsPreferences.autoRefresh, newsPreferences.refreshInterval, user]);

  // Initial news load
  useEffect(() => {
    if (userLocation) {
      fetchNewsData();
    }
  }, [userLocation]);

  const loadUserPreferences = () => {
    if (!user) return;
    
    const saved = localStorage.getItem(`news_preferences_${user.uid}`);
    if (saved) {
      setNewsPreferences(JSON.parse(saved));
    }
  };

  const loadUserLocation = async () => {
    if (!user) return;
    
    const savedLocation = localStorage.getItem(`user_location_${user.uid}`);
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      // Check if location is older than 24 hours
      const lastUpdated = new Date(location.lastUpdated);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff < 24) {
        setUserLocation(location);
        return;
      }
    }
    
    // Fetch fresh location
    await updateLocation();
  };

  const loadSavedArticles = () => {
    if (!user) return;
    
    const saved = localStorage.getItem(`saved_news_${user.uid}`);
    if (saved) {
      setSavedArticles(JSON.parse(saved));
    }
  };

  const fetchNewsData = async (category?: NewsCategory, country?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const newsData = await fetchNews(
        category, 
        country || userLocation?.countryCode,
        newsPreferences.preferredProviders
      );
      
      // Add local news if user location is available
      if (userLocation && !category) {
        const localNews = await fetchLocalNews(userLocation);
        newsData.push(...localNews);
      }
      
      // Sort by publication date and user preferences
      const sortedNews = newsData.sort((a, b) => {
        const aTime = new Date(a.publishedAt).getTime();
        const bTime = new Date(b.publishedAt).getTime();
        
        // Prioritize preferred providers
        const aProviderPriority = newsPreferences.preferredProviders.includes(a.source.id) ? 1 : 0;
        const bProviderPriority = newsPreferences.preferredProviders.includes(b.source.id) ? 1 : 0;
        
        if (aProviderPriority !== bProviderPriority) {
          return bProviderPriority - aProviderPriority;
        }
        
        return bTime - aTime;
      });
      
      setArticles(sortedNews);
    } catch (error) {
      console.error('Error fetching news:', error);
      setError('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  };

  const refreshNews = async () => {
    await fetchNewsData();
    toast.success('News updated successfully');
  };

  const updateLocation = async () => {
    if (!user) return;
    
    try {
      const location = await getUserLocation();
      if (location) {
        setUserLocation(location);
        localStorage.setItem(`user_location_${user.uid}`, JSON.stringify(location));
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };

  const updatePreferences = (preferences: Partial<NewsPreferences>) => {
    if (!user) return;
    
    const updated = { ...newsPreferences, ...preferences };
    setNewsPreferences(updated);
    localStorage.setItem(`news_preferences_${user.uid}`, JSON.stringify(updated));
  };

  const saveArticle = (article: NewsArticle) => {
    if (!user) {
      toast.error('Please log in to save articles');
      return;
    }
    
    const articleWithSaved = { ...article, isSaved: true };
    setSavedArticles(prev => {
      const exists = prev.some(saved => saved.id === article.id);
      if (exists) return prev;
      const updated = [articleWithSaved, ...prev];
      localStorage.setItem(`saved_news_${user.uid}`, JSON.stringify(updated));
      return updated;
    });
    
    toast.success('Article saved');
  };

  const unsaveArticle = (articleId: string) => {
    if (!user) return;
    
    setSavedArticles(prev => {
      const updated = prev.filter(article => article.id !== articleId);
      localStorage.setItem(`saved_news_${user.uid}`, JSON.stringify(updated));
      return updated;
    });
    
    toast.success('Article removed from saved');
  };

  const isArticleSaved = (articleId: string) => {
    return savedArticles.some(article => article.id === articleId);
  };

  const getNewsByCategory = (category: NewsCategory) => {
    return articles.filter(article => article.category === category);
  };

  return (
    <NewsContext.Provider value={{
      articles,
      savedArticles,
      loading,
      error,
      userLocation,
      newsPreferences,
      fetchNews: fetchNewsData,
      refreshNews,
      saveArticle,
      unsaveArticle,
      isArticleSaved,
      updateLocation,
      updatePreferences,
      getNewsByCategory
    }}>
      {children}
    </NewsContext.Provider>
  );
}

export function useNews() {
  const context = useContext(NewsContext);
  if (!context) {
    throw new Error('useNews must be used within a NewsProvider');
  }
  return context;
}